/**
 * Contact form validation.
 *
 * Messages are i18n key *suffixes* within the `contactForm.errors` namespace,
 * not display copy — the field renderer resolves them with
 * `t('errors.' + message)`. Keeping the translator out of this module is what
 * lets the rules be unit-tested with no React and no locale bundle, and it
 * keeps one rule in one place for both locales.
 *
 * The shape is deliberately flat rather than a discriminated union on
 * `variant`: an unselected service dropdown holds `''`, so a union's inferred
 * input type could not describe `CONTACT_FORM_DEFAULTS` without a second
 * hand-maintained type for react-hook-form to use. One schema whose input and
 * output shapes match means `useForm<ContactFormValues>` needs no parallel
 * type to drift out of sync.
 */
import { z } from "zod";

export const CONTACT_VARIANTS = ["service", "careers"] as const;
export type ContactVariant = (typeof CONTACT_VARIANTS)[number];

/**
 * The service-request categories. These slugs are both the dropdown's option
 * values and the wire value posted to `pages/api/contact.ts`, which does not
 * map them — it titleises the slug itself into the notification email's
 * subject and Service Type row. So a slug edited here changes what the shop
 * reads in its inbox, and its label there comes from `String.replace('-', ' ')`
 * with no `g` flag: a slug carrying a second hyphen would render half-converted.
 * Every slug below holds exactly one.
 */
export const SERVICE_TYPES = [
  "tire-service",
  "mechanic-service",
  "collision-repair",
] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];

/** Digits plus the punctuation people actually type in a phone number. */
const PHONE_PATTERN = /^[0-9()+\-\s]{7,20}$/;

/**
 * The message cap, exported because the field renders a countdown against it.
 * A counter that disagrees with the rule is worse than no counter, so both read
 * this — the rule below included.
 */
export const MESSAGE_MAX_LENGTH = 2000;

/**
 * How close to the cap the countdown appears. Below this the number is noise:
 * nobody writing three sentences needs to be told they have room for 1,900 more
 * characters.
 */
export const MESSAGE_COUNTER_THRESHOLD = 250;

/**
 * Every suffix this schema can emit, and the single source of truth for the
 * `contactForm.errors.*` keys the renderer resolves.
 *
 * Exported so the renderer can refuse to interpolate anything that is not a
 * member — a Zod default string would otherwise be namespaced and shown to a
 * visitor as `contactForm.errors.Invalid enum value…` — and so a locale test can
 * assert both bundles still answer every one of them.
 */
export const CONTACT_ERROR_KEYS = [
  "firstNameRequired",
  "firstNameTooLong",
  "lastNameTooLong",
  "emailRequired",
  "emailTooLong",
  "emailInvalid",
  "phoneInvalid",
  "phoneRequiredService",
  "messageTooShort",
  "messageTooLong",
  "privacyRequired",
  "serviceTypeRequired",
  "serviceTypeInvalid",
] as const;
export type ContactErrorKey = (typeof CONTACT_ERROR_KEYS)[number];

/**
 * An off-list service value must name a key, never a Zod default.
 *
 * The map is repeated on both union options, not just on the union itself: Zod's
 * union-level map only labels the wrapping `invalid_union` issue, while
 * `@hookform/resolvers` reports `unionErrors[0].errors[0].message` — the inner
 * option's message. Setting only the outer one leaves the string the visitor
 * actually sees as Zod's default.
 */
const serviceTypeErrorMap = () => ({ message: "serviceTypeInvalid" as const });

export const contactFormSchema = z
  .object({
    variant: z.enum(CONTACT_VARIANTS),

    // Trim before measuring: `min(1)` on an untrimmed string accepts a single
    // space, which reads as a name everywhere downstream while naming nobody.
    firstName: z
      .string()
      .trim()
      .min(1, "firstNameRequired")
      .max(80, "firstNameTooLong"),
    lastName: z.string().trim().max(80, "lastNameTooLong"),
    email: z
      .string()
      .trim()
      .min(1, "emailRequired")
      .max(254, "emailTooLong")
      .email("emailInvalid"),

    // Format only. Whether an empty value is acceptable depends on the variant,
    // so that half of the rule lives in the superRefine below; here the empty
    // string is exempted rather than matched, because an untouched input submits
    // '' and a format complaint is the wrong thing to say about a blank field.
    phoneNumber: z
      .string()
      .trim()
      .refine((value) => value === "" || PHONE_PATTERN.test(value), {
        message: "phoneInvalid",
      }),

    message: z
      .string()
      .trim()
      .min(10, "messageTooShort")
      .max(MESSAGE_MAX_LENGTH, "messageTooLong"),

    agreeToPrivacy: z.boolean(),

    // '' is the unselected state, which the superRefine below rejects for the
    // service variant. The careers variant leaves it '' legitimately.
    serviceType: z.union(
      [
        z.enum(SERVICE_TYPES, { errorMap: serviceTypeErrorMap }),
        z.literal("", { errorMap: serviceTypeErrorMap }),
      ],
      { errorMap: serviceTypeErrorMap },
    ),
    isNewCustomer: z.boolean(),
  })
  .superRefine((values, ctx) => {
    // A service request is answered by calling back with a quote or a time, so
    // one the shop can only reply to by email is one that stalls. A job
    // application has no such next step, and demanding a number to be considered
    // for a job would cost applications for nothing.
    if (values.variant === "service" && values.phoneNumber === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phoneNumber"],
        message: "phoneRequiredService",
      });
    }
    if (values.variant === "service" && values.serviceType === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["serviceType"],
        message: "serviceTypeRequired",
      });
    }
    if (!values.agreeToPrivacy) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["agreeToPrivacy"],
        message: "privacyRequired",
      });
    }
  });

export type ContactFormValues = z.infer<typeof contactFormSchema>;

/**
 * The service variant is preselected because it is the revenue path and the
 * overwhelming majority of traffic. Nothing here parses — an untouched form is
 * an invalid form.
 */
export const CONTACT_FORM_DEFAULTS: ContactFormValues = {
  variant: "service",
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  message: "",
  agreeToPrivacy: false,
  serviceType: "",
  isNewCustomer: false,
};
